/**
 * Shared function used in the profile completion feature only
 *
 * Used for filter out the answers with the question name and make sure work fine with question branching
 *
 * @param {Array} values - Formik's values object
 * @param {boolean} excludeFalsy - Exclude falsy answer
 */

export const remapAnswersWQuestions = (values, excludeFalsy) => {
	const renderedQuestionsElements = document.querySelectorAll('[data-opus-question]')

	const renderedQuestionData = Array.from(renderedQuestionsElements).map((element) => {
		return {
			dataName: element.getAttribute('data-name'),
			optional: element.getAttribute('data-optional'),
			quizQuestionId: element.getAttribute('data-opus-question'),
		}
	})

	// Get all required questions that need answers
	const requiredQuestions = renderedQuestionData?.filter((q) => q.optional === 'false').map((q) => q.quizQuestionId)

	// Get all answered question IDs
	const answeredQuestionIds = values?.filter((v) => v?.answer || (v?.files && v?.files.length > 0)).map((v) => v?.quizQuestionId)

	// Check if all required questions are answered
	const allRequiredAnswered = requiredQuestions?.every((qId) => answeredQuestionIds?.includes(qId))

	if (allRequiredAnswered) {
		return values.map(() => true)
	}

	return values?.filter((value) => {
		if (excludeFalsy && !value?.answer && value?.files.length === 0) {
			return false
		}

		if (value?.answer === 'AVOID_ANSWER_BLANK' && value?.files.length === 0) {
			return false // Ignore case where the user has removed file(s)
		}

		return true
	})
}

export const isAllAnswersCompleted = (values) => {
	const renderedQuestionsElements = document.querySelectorAll('[data-opus-question]')
	const renderedQuestionData = Array.from(renderedQuestionsElements).map((element) => {
		return {
			dataName: element.getAttribute('data-name'),
			optional: element.getAttribute('data-optional'),
			quizQuestionId: element.getAttribute('data-opus-question'),
		}
	})

	// Get all required questions that need answers
	const requiredQuestions = renderedQuestionData?.filter((q) => q.optional === 'false').map((q) => q.quizQuestionId)

	// Get all answered question IDs - check for valid answer or files with valid file data
	const answeredQuestionIds = values
		?.filter((v) => {
			// Check if has a valid text answer (not empty, not AVOID_ANSWER_BLANK)
			const hasValidAnswer = v?.answer && v?.answer !== '' && v?.answer !== 'AVOID_ANSWER_BLANK'

			// Check if has valid files (files array with at least one file that has fileUrl or blobId)
			// Structure from API: files[].file.fileUrl or files[].file.blobId
			// Structure from upload: files[].fileSignedBlobId
			const hasValidFiles =
				v?.files &&
				v?.files.length > 0 &&
				v?.files.some((f) => {
					const hasApiFile = f?.file?.fileUrl || f?.file?.blobId
					const hasUploadFile = f?.fileSignedBlobId
					return (hasApiFile || hasUploadFile) && !f?._destroy
				})

			return hasValidAnswer || hasValidFiles
		})
		.map((v) => v?.quizQuestionId)

	// Check if all required questions are answered
	const allRequiredAnswered = requiredQuestions?.every((qId) => answeredQuestionIds?.includes(qId))

	if (allRequiredAnswered) {
		return true
	}

	return false
}

/**
 * Function update the answer to current answers object
 * 1. If the answer is already in the answers object, update it
 * 2. If the answer is not in the answers object, add it
 *
 * @param {Array} answers - The current answers object
 * @param {Object} newAnswer - The new answer object
 */
export const updateAnswers = (answers, newAnswer) => {
	const index = answers.findIndex((answer) => answer.quizQuestionId === newAnswer.quizQuestionId)

	if (index > -1) {
		answers[index] = newAnswer
	} else {
		answers.push(newAnswer)
	}

	return answers
}
